// src/pages/app/ProfileModal.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import "./Dashboard.css";
import { auth, db } from "../../lib/firebase";
import {
  doc,
  getDoc,
  setDoc,
  collection,
  query,
  where,
  limit,
  getDocs,
} from "firebase/firestore";

import {
  getMyProfile,
  updateMyProfile,
  uploadProfilePhoto,
  uploadSignaturePng,
  uploadCredentialFile,
  setMyPinWithCallable,
} from "../../lib/firebase";

const POSITION_LICENSES = ["EMR", "EMT", "AEMT", "PARAMEDIC", "RN", "PA", "APRN", "PHYSICIAN"];
const NON_REQUIRED_POSITIONS = ["STUDENT", "VOLUNTEER", "RESIDENT", "ADMIN", "SUPER USER", "SUPERUSER"];
const PROVIDER_POSITIONS = ["PA", "APRN", "PHYSICIAN"];
const SPONSOR_HOSPITAL_POSITIONS = ["PARAMEDIC"];

const ALL_UPLOAD_TYPES = [
  "BLS",
  "ACLS",
  "PALS",
  "ICS100",
  "EMR",
  "EMT",
  "AEMT",
  "PARAMEDIC",
  "RN",
  "PA",
  "APRN",
  "PHYSICIAN",
  "DRIVERS_LICENSE",
  "STATE_LICENSE",
  "MALPRACTICE",
  "OTHER",
];

function tsToDate(x) {
  if (!x) return null;
  if (x?.toDate) return x.toDate();
  if (x?.seconds) return new Date(x.seconds * 1000);
  return x instanceof Date ? x : new Date(x);
}

function formatDate(d) {
  const x = tsToDate(d);
  if (!x || Number.isNaN(x.getTime())) return "—";
  return x.toLocaleDateString();
}

function isExpired(expiresAt) {
  const d = tsToDate(expiresAt);
  if (!d) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return d.getTime() < today.getTime();
}

function normalizeType(value = "") {
  return String(value || "").trim().toUpperCase();
}

function getProfessionalLicenseForPosition(positionRaw = "") {
  const p = normalizeType(positionRaw);
  if (POSITION_LICENSES.includes(p)) return p;
  return null;
}

function requiredByPosition(positionRaw = "") {
  const p = normalizeType(positionRaw);

  if (!p) return [];
  if (NON_REQUIRED_POSITIONS.includes(p)) return [];

  if (p === "TECH") return ["BLS"];
  if (p === "EMR") return ["EMR", "BLS", "ICS100"];
  if (p === "EMT") return ["EMT", "BLS", "ICS100"];
  if (p === "AEMT") return ["AEMT", "BLS", "ICS100"];
  if (p === "RN") return ["RN", "BLS"];
  if (p === "PA") return ["PA", "BLS"];
  if (p === "APRN") return ["APRN", "BLS"];
  if (p === "PHYSICIAN") return ["PHYSICIAN"];
  if (p === "PARAMEDIC") return ["PARAMEDIC", "ICS100", "BLS", "ACLS", "PALS"];

  return [];
}

function parseCombinedAddress(raw = "") {
  const value = String(raw || "").trim();
  if (!value) {
    return { street: "", city: "", state: "", zip: "" };
  }

  const m = value.match(
    /^(.*?)(?:,\s*|\s+)([A-Za-z.\-'\s]+?)\s+([A-Z]{2})\s+(\d{5}(?:-\d{4})?)$/
  );

  if (m) {
    return {
      street: String(m[1] || "").trim(),
      city: String(m[2] || "").trim(),
      state: String(m[3] || "").trim(),
      zip: String(m[4] || "").trim(),
    };
  }

  return {
    street: value,
    city: "",
    state: "",
    zip: "",
  };
}

function firstNonEmpty(...vals) {
  for (const v of vals) {
    if (String(v ?? "").trim()) return String(v).trim();
  }
  return "";
}

function parseFullName(raw = "") {
  const value = String(raw || "").trim();
  if (!value) return { firstName: "", lastName: "" };
  const parts = value.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return { firstName: parts[0], lastName: "" };
  return {
    firstName: parts.slice(0, -1).join(" "),
    lastName: parts.slice(-1).join(""),
  };
}

function digitsOnly(value = "") {
  return String(value || "").replace(/\D/g, "");
}

function formatPhoneInput(value = "") {
  const digits = digitsOnly(value).slice(0, 10);

  if (!digits) return "";
  if (digits.length < 4) return `(${digits}`;
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
}

function formatZipInput(value = "") {
  const cleaned = String(value || "").replace(/[^\d-]/g, "").slice(0, 10);
  return cleaned;
}

function toTitleCase(value = "") {
  return String(value || "")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function statusToBool(statusValue) {
  const s = String(statusValue || "").trim().toLowerCase();
  if (s === "active") return true;
  if (s === "inactive") return false;
  return true;
}

function boolToStatus(activeValue) {
  return activeValue === false ? "Inactive" : "Active";
}

async function findApplicationForProfile(uid, profile = {}) {
  const possibleEmailKeys = [
    profile?.sourceApplicationId,
    profile?.email,
    profile?.applicationData?.email,
    auth.currentUser?.email,
  ]
    .map((x) => String(x || "").trim().toLowerCase())
    .filter(Boolean);

  for (const emailKey of possibleEmailKeys) {
    const snap = await getDoc(doc(db, "2026_Applications", emailKey));
    if (snap.exists()) return { id: snap.id, data: snap.data() };
  }

  const fieldsToTry = ["uid", "userId", "authUid"];
  for (const field of fieldsToTry) {
    const q = query(
      collection(db, "2026_Applications"),
      where(field, "==", uid),
      limit(1)
    );

    const snap = await getDocs(q);
    if (!snap.empty) {
      const d = snap.docs[0];
      return { id: d.id, data: d.data() };
    }
  }

  return { id: "", data: null };
}

export default function ProfileModal({
  open,
  onClose,
  userId = null,
  canEdit = true,
}) {
  const currentUid = auth.currentUser?.uid;
  const uid = userId || currentUid;
  const isEditingSelf = !userId || userId === currentUid;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const [profile, setProfile] = useState(null);
  const [payConfig, setPayConfig] = useState(null);

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    employeeId: "",
    position: "",
    role: "",
    status: "Active",
    thEmployeeStatus: "No",
    currentServiceEmployer: "",
    sponsorHospital: "",

    street: "",
    city: "",
    state: "",
    zip: "",

    businessStreet: "",
    businessCity: "",
    businessState: "",
    businessZip: "",
    businessPhone: "",
    npiNumber: "",
    deaNumber: "",

    emergencyContactName: "",
    emergencyContactRelationship: "",
    emergencyContactPhone: "",

    pin: "",
  });

  const [credFileBusy, setCredFileBusy] = useState(false);
  const [credModalOpen, setCredModalOpen] = useState(false);
  const [credType, setCredType] = useState("BLS");
  const [credExpires, setCredExpires] = useState("");
  const [credFile, setCredFile] = useState(null);

  const sigWrapRef = useRef(null);
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastPtRef = useRef({ x: 0, y: 0 });
  const [editingSignature, setEditingSignature] = useState(false);

  function setField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  useEffect(() => {
    if (!open) return;
    let alive = true;

    (async () => {
      setLoading(true);
      setErr("");

      try {
        if (!uid) throw new Error("No user selected.");

        const p = await getMyProfile(uid);
        if (!alive) return;

        try {
          const paySnap = await getDoc(doc(db, "appConfig", "payRates"));
          if (paySnap.exists()) setPayConfig(paySnap.data());
          else setPayConfig(null);
        } catch (e) {
          console.error("Failed loading payRates config:", e);
          setPayConfig(null);
        }

        setProfile(p);

       const foundApp = await findApplicationForProfile(uid, p);
const app = foundApp.data;

        const appData = p?.applicationData || {};

        const nameFromProfile = firstNonEmpty(
          `${p?.firstName || ""} ${p?.lastName || ""}`.trim(),
          p?.name,
          p?.displayName,
          `${appData?.firstName || ""} ${appData?.lastName || ""}`.trim(),
          appData?.name,
          `${app?.firstName || ""} ${app?.lastName || ""}`.trim(),
          app?.name
        );
        const parsedName = parseFullName(nameFromProfile);

        const combinedAddress = firstNonEmpty(
          p?.address,
          appData?.address,
          app?.address
        );
        const parsedAddress = parseCombinedAddress(combinedAddress);

        const businessCombinedAddress = firstNonEmpty(
          p?.businessAddress,
          appData?.businessAddress,
          app?.businessAddress
        );
        const parsedBusinessAddress = parseCombinedAddress(businessCombinedAddress);

        const resolvedStatus = firstNonEmpty(
        p?.status,
        appData?.status,
        app?.status,
        p?.active !== undefined ? boolToStatus(p.active) : "",
        appData?.active !== undefined ? boolToStatus(appData.active) : "",
        app?.active !== undefined ? boolToStatus(app.active) : "",
        "Active"
);

        const resolvedPosition = firstNonEmpty(
          p?.position,
          appData?.position,
          app?.position
        );

        const resolvedRole = firstNonEmpty(
          p?.role,
          appData?.role,
          app?.role
        );

        setForm({
          firstName: firstNonEmpty(p?.firstName, appData?.firstName, app?.firstName, parsedName.firstName),
          lastName: firstNonEmpty(p?.lastName, appData?.lastName, app?.lastName, parsedName.lastName),
          email: firstNonEmpty(p?.email, appData?.email, app?.email, auth.currentUser?.email),
          phone: formatPhoneInput(
            firstNonEmpty(
              p?.phone,
              appData?.phone,
              appData?.cellPhone,
              appData?.mobile,
              app?.phone,
              app?.cellPhone,
              app?.mobile
            )
          ),
          employeeId: firstNonEmpty(
            p?.employeeId,
            p?.employeeNumber,
            appData?.employeeId,
            appData?.employeeNumber,
            app?.employeeId,
            app?.employeeNumber
          ),
          position: resolvedPosition,
          role: resolvedRole,
          status: /^inactive$/i.test(resolvedStatus) ? "Inactive" : "Active",
          thEmployeeStatus: /^yes$/i.test(
            firstNonEmpty(
              p?.thEmployeeStatus,
              p?.isTHEmployee,
              p?.thEmployee,
              appData?.thEmployeeStatus,
              appData?.isTHEmployee,
              appData?.thEmployee,
              app?.thEmployeeStatus,
              app?.isTHEmployee,
              app?.thEmployee,
              "No"
            )
          )
            ? "Yes"
            : firstNonEmpty(
                p?.thEmployeeStatus,
                p?.isTHEmployee,
                p?.thEmployee,
                appData?.thEmployeeStatus,
                appData?.isTHEmployee,
                appData?.thEmployee,
                app?.thEmployeeStatus,
                app?.isTHEmployee,
                app?.thEmployee,
                "No"
              ) === true
            ? "Yes"
            : "No",
          currentServiceEmployer: firstNonEmpty(
            p?.currentServiceEmployer,
            p?.serviceEmployer,
            p?.employer,
            appData?.currentServiceEmployer,
            appData?.serviceEmployer,
            appData?.employer,
            app?.currentServiceEmployer,
            app?.serviceEmployer,
            app?.employer
          ),
          sponsorHospital: firstNonEmpty(
            p?.sponsorHospital,
            appData?.sponsorHospital,
            app?.sponsorHospital
          ),

          street: firstNonEmpty(p?.street, appData?.street, app?.street, parsedAddress.street),
          city: firstNonEmpty(p?.city, appData?.city, app?.city, parsedAddress.city),
          state: firstNonEmpty(p?.state, appData?.state, app?.state, parsedAddress.state),
          zip: firstNonEmpty(
            p?.zip,
            p?.zipcode,
            appData?.zip,
            appData?.zipcode,
            app?.zip,
            app?.zipcode,
            parsedAddress.zip
          ),

          businessStreet: firstNonEmpty(
            p?.businessStreet,
            appData?.businessStreet,
            app?.businessStreet,
            parsedBusinessAddress.street
          ),
          businessCity: firstNonEmpty(
            p?.businessCity,
            appData?.businessCity,
            app?.businessCity,
            parsedBusinessAddress.city
          ),
          businessState: firstNonEmpty(
            p?.businessState,
            appData?.businessState,
            app?.businessState,
            parsedBusinessAddress.state
          ),
          businessZip: firstNonEmpty(
            p?.businessZip,
            appData?.businessZip,
            app?.businessZip,
            parsedBusinessAddress.zip
          ),
          businessPhone: formatPhoneInput(
            firstNonEmpty(
              p?.businessPhone,
              appData?.businessPhone,
              app?.businessPhone
            )
          ),
          npiNumber: firstNonEmpty(p?.npiNumber, p?.npi, appData?.npiNumber, appData?.npi, app?.npiNumber, app?.npi),
          deaNumber: firstNonEmpty(p?.deaNumber, p?.dea, appData?.deaNumber, appData?.dea, app?.deaNumber, app?.dea),

          emergencyContactName: firstNonEmpty(
            p?.emergencyContactName,
            appData?.emergencyContactName,
            app?.emergencyContactName
          ),
          emergencyContactRelationship: firstNonEmpty(
            p?.emergencyContactRelationship,
            appData?.emergencyContactRelationship,
            app?.emergencyContactRelationship
          ),
          emergencyContactPhone: formatPhoneInput(
            firstNonEmpty(
              p?.emergencyContactPhone,
              appData?.emergencyContactPhone,
              app?.emergencyContactPhone
            )
          ),

          pin: "",
        });
      } catch (e) {
        console.error(e);
        if (!alive) return;
        setErr(e?.message || "Could not load profile.");
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [open, uid]);

  const position = form.position || "";
  const email = form.email || profile?.email || auth.currentUser?.email || "";
  const providerFieldsVisible = PROVIDER_POSITIONS.includes(normalizeType(position));
  const sponsorHospitalVisible = SPONSOR_HOSPITAL_POSITIONS.includes(normalizeType(position));

  const payDisplay = useMemo(() => {
    if (!payConfig) return "—";

    const base = payConfig?.positions?.[position];
    if (base === undefined || base === null) return "—";
    if (typeof base !== "number") return String(base);

    let rate = base;
    const bump = Number(payConfig?.supervisorBump || 0);

    if (String(form.role || "").toLowerCase() === "supervisor") {
      rate += bump;
    }

    return `$${rate.toFixed(2)}/hr`;
  }, [payConfig, position, form.role]);

  const requiredList = useMemo(() => requiredByPosition(position), [position]);
  const creds = Array.isArray(profile?.credentials) ? profile.credentials : [];

  const credIndex = useMemo(() => {
    const m = new Map();
    for (const c of creds) {
      const key = normalizeType(c?.type);
      if (!key) continue;
      m.set(key, c);
    }
    return m;
  }, [creds]);

  const allUploadTypes = useMemo(() => {
    const positionLicense = getProfessionalLicenseForPosition(position);
    return Array.from(
      new Set(
        [
          ...requiredList,
          ...(positionLicense ? [positionLicense] : []),
          ...ALL_UPLOAD_TYPES,
          ...creds.map((c) => normalizeType(c?.type)),
        ].filter(Boolean)
      )
    );
  }, [requiredList, position, creds]);

  const checklist = useMemo(() => {
    return requiredList.map((t) => {
      const key = normalizeType(t);
      const item = credIndex.get(key);
      const missing = !item?.fileURL;
      const expired = item?.expiresAt ? isExpired(item.expiresAt) : false;
      return {
        type: key,
        missing,
        expired,
        expiresAt: item?.expiresAt || null,
      };
    });
  }, [requiredList, credIndex]);

  function configureSigCtx(ctx) {
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#111827";
  }

  function getWrapInnerBox() {
    const wrap = sigWrapRef.current;
    if (!wrap) return null;

    const rect = wrap.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;

    const cs = window.getComputedStyle(wrap);
    const borderL = parseFloat(cs.borderLeftWidth) || 0;
    const borderR = parseFloat(cs.borderRightWidth) || 0;
    const borderT = parseFloat(cs.borderTopWidth) || 0;
    const borderB = parseFloat(cs.borderBottomWidth) || 0;

    const innerW = rect.width - borderL - borderR;
    const innerH = rect.height - borderT - borderB;
    if (innerW <= 0 || innerH <= 0) return null;

    return { rect, borderL, borderT, innerW, innerH };
  }

  function resizeSignatureCanvas(preserve = true) {
    const canvas = canvasRef.current;
    const box = getWrapInnerBox();
    if (!canvas || !box) return;

    const { innerW, innerH } = box;
    const dpr = window.devicePixelRatio || 1;

    let snapshot = null;
    if (preserve) {
      try {
        snapshot = canvas.toDataURL("image/png");
      } catch {
        snapshot = null;
      }
    }

    canvas.width = Math.round(innerW * dpr);
    canvas.height = Math.round(innerH * dpr);

    const ctx = canvas.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    configureSigCtx(ctx);

    if (snapshot) {
      const img = new Image();
      img.onload = () => {
        ctx.clearRect(0, 0, innerW, innerH);
        ctx.drawImage(img, 0, 0, innerW, innerH);
      };
      img.src = snapshot;
    } else {
      ctx.clearRect(0, 0, innerW, innerH);
    }
  }

  useEffect(() => {
    if (!open || !editingSignature) return;

    const raf1 = requestAnimationFrame(() => {
      requestAnimationFrame(() => resizeSignatureCanvas(true));
    });

    const onResize = () => resizeSignatureCanvas(true);
    window.addEventListener("resize", onResize);

    let ro;
    if ("ResizeObserver" in window && sigWrapRef.current) {
      ro = new ResizeObserver(() => resizeSignatureCanvas(true));
      ro.observe(sigWrapRef.current);
    }

    return () => {
      cancelAnimationFrame(raf1);
      window.removeEventListener("resize", onResize);
      if (ro) ro.disconnect();
    };
  }, [open, editingSignature]);

  function getCanvasPointFromPointerEvent(e) {
    const box = getWrapInnerBox();
    if (!box) return { x: 0, y: 0 };

    const { rect, borderL, borderT } = box;
    return {
      x: e.clientX - rect.left - borderL,
      y: e.clientY - rect.top - borderT,
    };
  }

  function startDraw(e) {
    const canvas = canvasRef.current;
    if (!canvas) return;

    resizeSignatureCanvas(true);

    try {
      canvas.setPointerCapture?.(e.pointerId);
    } catch {}

    drawingRef.current = true;
    lastPtRef.current = getCanvasPointFromPointerEvent(e);

    const ctx = canvas.getContext("2d");
    configureSigCtx(ctx);
  }

  function moveDraw(e) {
    if (!drawingRef.current) return;
    e.preventDefault?.();

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    configureSigCtx(ctx);

    const pt = getCanvasPointFromPointerEvent(e);
    const last = lastPtRef.current;

    ctx.beginPath();
    ctx.moveTo(last.x, last.y);
    ctx.lineTo(pt.x, pt.y);
    ctx.stroke();

    lastPtRef.current = pt;
  }

  function endDraw() {
    drawingRef.current = false;
  }

  function clearSignature() {
    const canvas = canvasRef.current;
    const box = getWrapInnerBox();
    if (!canvas || !box) return;

    const { innerW, innerH } = box;
    const ctx = canvas.getContext("2d");
    configureSigCtx(ctx);
    ctx.clearRect(0, 0, innerW, innerH);
  }

  async function refreshProfile() {
    const p = await getMyProfile(uid);
    setProfile(p);
  }

  async function handleSaveSignature() {
    try {
      setErr("");
      setSaving(true);

      const canvas = canvasRef.current;
      if (!canvas) throw new Error("Signature pad not available.");

      const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
      if (!blob) throw new Error("Could not create signature image.");

      const url = await uploadSignaturePng(uid, blob);
      await updateMyProfile(uid, { signatureURL: url });
      await refreshProfile();
      setEditingSignature(false);
    } catch (e) {
      console.error(e);
      setErr(e?.message || "Could not save signature.");
    } finally {
      setSaving(false);
    }
  }

  async function handlePhotoFile(file) {
    if (!file) return;
    try {
      setErr("");
      setSaving(true);

      const url = await uploadProfilePhoto(uid, file);
      await updateMyProfile(uid, { photoURL: url });

      await refreshProfile();
    } catch (e) {
      console.error(e);
      setErr(e?.message || "Could not upload photo.");
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveEdits() {
    try {
      setErr("");
      setSaving(true);

      const firstName = String(form.firstName || "").trim();
      const lastName = String(form.lastName || "").trim();
      const street = String(form.street || "").trim();
      const city = String(form.city || "").trim();
      const stateValue = String(form.state || "").trim().toUpperCase();
      const zip = formatZipInput(form.zip);
      const phone = formatPhoneInput(form.phone);
      const businessPhone = formatPhoneInput(form.businessPhone);
      const emergencyContactPhone = formatPhoneInput(form.emergencyContactPhone);

      if (!firstName || !lastName) {
        throw new Error("First and last name are required.");
      }

      if (!street || !city || !stateValue || !zip) {
        throw new Error("Street, City, State, and ZIP are required.");
      }

      const fullName = `${firstName} ${lastName}`.trim();
      const combinedAddress = `${street}, ${city} ${stateValue} ${zip}`.trim();

      const businessStreet = String(form.businessStreet || "").trim();
      const businessCity = String(form.businessCity || "").trim();
      const businessState = String(form.businessState || "").trim().toUpperCase();
      const businessZip = formatZipInput(form.businessZip);

      const hasBusinessAddress =
        businessStreet || businessCity || businessState || businessZip;

      const businessAddress = hasBusinessAddress
        ? `${businessStreet}, ${businessCity} ${businessState} ${businessZip}`.trim()
        : "";

      const normalizedStatus = form.status === "Inactive" ? "Inactive" : "Active";
      const activeBool = statusToBool(normalizedStatus);
      const thEmployeeYes = form.thEmployeeStatus === "Yes";

      const patch = {
        firstName,
        lastName,
        name: fullName,
        displayName: fullName,
        email: String(form.email || "").trim(),
        phone,
        employeeId: String(form.employeeId || "").trim(),
        employeeNumber: String(form.employeeId || "").trim(),
        position: String(form.position || "").trim(),
        role: String(form.role || "").trim(),
        status: normalizedStatus,
        active: activeBool,
        thEmployeeStatus: thEmployeeYes ? "Yes" : "No",
        isTHEmployee: thEmployeeYes,
        thEmployee: thEmployeeYes ? "Yes" : "No",
        currentServiceEmployer: String(form.currentServiceEmployer || "").trim(),
        serviceEmployer: String(form.currentServiceEmployer || "").trim(),
        employer: String(form.currentServiceEmployer || "").trim(),
        sponsorHospital: sponsorHospitalVisible
          ? String(form.sponsorHospital || "").trim()
          : "",

        address: combinedAddress,
        street,
        city,
        state: stateValue,
        zip,
        zipcode: zip,

        npiNumber: providerFieldsVisible ? String(form.npiNumber || "").trim() : "",
        npi: providerFieldsVisible ? String(form.npiNumber || "").trim() : "",
        deaNumber: providerFieldsVisible ? String(form.deaNumber || "").trim() : "",
        dea: providerFieldsVisible ? String(form.deaNumber || "").trim() : "",
        businessStreet: providerFieldsVisible ? businessStreet : "",
        businessCity: providerFieldsVisible ? businessCity : "",
        businessState: providerFieldsVisible ? businessState : "",
        businessZip: providerFieldsVisible ? businessZip : "",
        businessAddress: providerFieldsVisible ? businessAddress : "",
        businessPhone: providerFieldsVisible ? businessPhone : "",

        emergencyContactName: String(form.emergencyContactName || "").trim(),
        emergencyContactRelationship: String(form.emergencyContactRelationship || "").trim(),
        emergencyContactPhone,
      };

      const applicationDataPatch = {
        ...patch,
      };

      await updateMyProfile(uid, patch);
      await updateMyProfile(uid, { applicationData: { ...(profile?.applicationData || {}), ...applicationDataPatch } });

      await setDoc(doc(db, "users", uid), patch, { merge: true });

      const emailKey = String(
        profile?.sourceApplicationId || profile?.email || form.email || ""
      )
        .trim()
        .toLowerCase();

      if (emailKey) {
        try {
          await setDoc(
            doc(db, "2026_Applications", emailKey),
            {
              ...applicationDataPatch,
              applicationData: {
                ...(profile?.applicationData || {}),
                ...applicationDataPatch,
              },
            },
            { merge: true }
          );
        } catch (syncErr) {
          console.warn("[ProfileModal] Could not sync to 2026_Applications:", syncErr);
        }
      }

      if (form.pin && String(form.pin).trim().length >= 4 && isEditingSelf) {
        await setMyPinWithCallable(uid, String(form.pin).trim());
        setField("pin", "");
      }

      await refreshProfile();
    } catch (e) {
      console.error(e);
      setErr(e?.message || "Could not save profile.");
    } finally {
      setSaving(false);
    }
  }

  function openCredModal(typeHint = null) {
    const hint = normalizeType(typeHint);
    const defaultType =
      hint ||
      checklist.find((x) => x.missing)?.type ||
      allUploadTypes[0] ||
      "OTHER";

    setCredType(defaultType);

    const existing = credIndex.get(defaultType);
    if (existing?.expiresAt) {
      const d = tsToDate(existing.expiresAt);
      if (d && !Number.isNaN(d.getTime())) {
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        setCredExpires(`${yyyy}-${mm}-${dd}`);
      } else {
        setCredExpires("");
      }
    } else {
      setCredExpires("");
    }

    setCredFile(null);
    setCredModalOpen(true);
  }

  function closeCredModal() {
    if (credFileBusy) return;
    setCredModalOpen(false);
    setCredFile(null);
  }

  async function handleSaveCredential() {
    try {
      setErr("");
      setCredFileBusy(true);

      const type = normalizeType(credType);
      if (!type) throw new Error("Select what you're uploading.");

      let expDate = null;
      if (type !== "ICS100" && type !== "OTHER" && type !== "DRIVERS_LICENSE") {
        if (!credExpires) throw new Error("Enter an expiration date.");
        expDate = new Date(`${credExpires}T00:00:00`);
        if (Number.isNaN(expDate.getTime())) throw new Error("Invalid expiration date.");
      }

      const existing = credIndex.get(type);

      if (!credFile) {
        if (!existing?.fileURL) {
          throw new Error("Choose a PDF or photo to upload.");
        }

        const next = (Array.isArray(profile?.credentials) ? profile.credentials : []).filter(
          (c) => normalizeType(c?.type) !== type
        );

        next.push({
          ...existing,
          type,
          expiresAt: expDate,
          updatedAt: new Date(),
        });

        await updateMyProfile(uid, { credentials: next });
        await refreshProfile();
        setCredModalOpen(false);
        return;
      }

      const file = credFile;
      const ok =
        file.type?.startsWith("image/") ||
        file.type === "application/pdf" ||
        file.name?.toLowerCase().endsWith(".pdf");

      if (!ok) throw new Error("Please upload a PDF or an image file.");

      const url = await uploadCredentialFile(
        uid,
        type,
        expDate ? credExpires : "no-expiration",
        file
      );

      const existingArr = Array.isArray(profile?.credentials) ? profile.credentials : [];
      const next = existingArr.filter((c) => normalizeType(c?.type) !== type);

      next.push({
        ...(existing || {}),
        type,
        expiresAt: expDate,
        fileURL: url,
        fileName: file.name || "",
        fileType: file.type || "",
        uploadedAt: existing?.uploadedAt || new Date(),
        updatedAt: new Date(),
      });

      await updateMyProfile(uid, { credentials: next });
      await refreshProfile();

      setCredModalOpen(false);
      setCredFile(null);
    } catch (e) {
      console.error(e);
      setErr(e?.message || "Could not save credential.");
    } finally {
      setCredFileBusy(false);
    }
  }

  if (!open) return null;

  return (
    <div className="tmModalBackdrop" onClick={onClose}>
      <div className="tmModal tmModal--wide" onClick={(e) => e.stopPropagation()}>
        <div className="tmModalHeader">
          <div className="tmModalTitle">
            {userId && !isEditingSelf ? "Edit Employee Profile" : "Update Profile"}
          </div>
          <button className="tmModalClose" onClick={onClose}>
            ✕
          </button>
        </div>

        <div style={{ padding: 14 }}>
          {loading ? (
            <div>Loading…</div>
          ) : (
            <>
              {err ? <div style={{ color: "#b91c1c", marginBottom: 10 }}>{err}</div> : null}

              <div className="tmTiles" style={{ gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                <div className="tmTile">
                  <div className="tmTileLabel">Position</div>
                  <div className="tmTileValueSmall">{form.position || "—"}</div>
                  <div className="tmTileHint">Pay: {payDisplay}</div>
                </div>

                <div className="tmTile">
                  <div className="tmTileLabel">Email</div>
                  <div className="tmTileValueSmall">{email || "—"}</div>
                </div>

                <div className="tmTile">
                  <div className="tmTileLabel">Required Items</div>
                  <div className="tmTileValueSmall">
                    {requiredList.length ? requiredList.join(", ") : "None required"}
                  </div>
                  <div className="tmTileHint">
                    Missing: {checklist.filter((x) => x.missing).length} • Expired:{" "}
                    {checklist.filter((x) => x.expired).length}
                  </div>
                </div>
              </div>

              <div className="tmCard" style={{ marginTop: 12 }}>
                <div className="tmCardHead">
                  <div>
                    <div className="tmCardTitle">Employee Information</div>
                    <div className="tmCardSub">Everything below is editable</div>
                  </div>
                </div>

                <div style={{ padding: 12, display: "grid", gap: 10 }}>
                  <div className="tmProfileGrid-3">
                    <label style={{ display: "grid", gap: 6 }}>
                      <div style={{ fontWeight: 600 }}>First Name *</div>
                      <input
                        className="tmInput"
                        value={form.firstName}
                        onChange={(e) => setField("firstName", e.target.value)}
                        disabled={!canEdit}
                      />
                    </label>

                    <label style={{ display: "grid", gap: 6 }}>
                      <div style={{ fontWeight: 600 }}>Last Name *</div>
                      <input
                        className="tmInput"
                        value={form.lastName}
                        onChange={(e) => setField("lastName", e.target.value)}
                        disabled={!canEdit}
                      />
                    </label>

                    <label style={{ display: "grid", gap: 6 }}>
                      <div style={{ fontWeight: 600 }}>Employee ID</div>
                      <input
                        className="tmInput"
                        value={form.employeeId}
                        onChange={(e) => setField("employeeId", e.target.value)}
                        disabled={!canEdit}
                      />
                    </label>
                  </div>

                  <div className="tmProfileGrid-3">
                    <label style={{ display: "grid", gap: 6 }}>
                      <div style={{ fontWeight: 600 }}>Email</div>
                      <input
                        className="tmInput"
                        type="email"
                        value={form.email}
                        onChange={(e) => setField("email", e.target.value)}
                        disabled={!canEdit}
                      />
                    </label>

                    <label style={{ display: "grid", gap: 6 }}>
                      <div style={{ fontWeight: 600 }}>Phone</div>
                      <input
                        className="tmInput"
                        value={form.phone}
                        onChange={(e) => setField("phone", formatPhoneInput(e.target.value))}
                        placeholder="(555) 555-5555"
                        disabled={!canEdit}
                      />
                    </label>

                    <label style={{ display: "grid", gap: 6 }}>
                      <div style={{ fontWeight: 600 }}>TH Employee</div>
                      <select
                        className="tmInput"
                        value={form.thEmployeeStatus}
                        onChange={(e) => setField("thEmployeeStatus", e.target.value)}
                        disabled={!canEdit}
                      >
                        <option value="No">No</option>
                        <option value="Yes">Yes</option>
                      </select>
                    </label>
                  </div>

                  <div className="tmProfileGrid-3">
                    <label style={{ display: "grid", gap: 6 }}>
                      <div style={{ fontWeight: 600 }}>Position / Level</div>
                      <input
                        className="tmInput"
                        value={form.position}
                        onChange={(e) => setField("position", e.target.value)}
                        disabled={!canEdit}
                      />
                    </label>

                    <label style={{ display: "grid", gap: 6 }}>
                      <div style={{ fontWeight: 600 }}>Role / Access</div>
                      <input
                        className="tmInput"
                        value={form.role}
                        onChange={(e) => setField("role", e.target.value)}
                        disabled={!canEdit}
                      />
                    </label>

                    <label style={{ display: "grid", gap: 6 }}>
                      <div style={{ fontWeight: 600 }}>Status</div>
                      <select
                        className="tmInput"
                        value={form.status}
                        onChange={(e) => setField("status", e.target.value)}
                        disabled={!canEdit}
                      >
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                      </select>
                    </label>
                  </div>

                  <div className="tmProfileGrid-3">
                    <label style={{ display: "grid", gap: 6, gridColumn: "1 / span 2" }}>
                      <div style={{ fontWeight: 600 }}>Current Service / Employer</div>
                      <input
                        className="tmInput"
                        value={form.currentServiceEmployer}
                        onChange={(e) => setField("currentServiceEmployer", e.target.value)}
                        disabled={!canEdit}
                      />
                    </label>

                    {sponsorHospitalVisible ? (
                      <label style={{ display: "grid", gap: 6 }}>
                        <div style={{ fontWeight: 600 }}>Sponsor Hospital</div>
                        <input
                          className="tmInput"
                          value={form.sponsorHospital}
                          onChange={(e) => setField("sponsorHospital", e.target.value)}
                          disabled={!canEdit}
                        />
                      </label>
                    ) : (
                      <div />
                    )}
                  </div>
                </div>
              </div>

              <div className="tmCard" style={{ marginTop: 12 }}>
                <div className="tmCardHead">
                  <div>
                    <div className="tmCardTitle">Home Address</div>
                    <div className="tmCardSub">Separate street, city, state, and ZIP fields</div>
                  </div>
                </div>

                <div style={{ padding: 12, display: "grid", gap: 10 }}>
                  <label style={{ display: "grid", gap: 6 }}>
                    <div style={{ fontWeight: 600 }}>Street *</div>
                    <input
                      className="tmInput"
                      value={form.street}
                      onChange={(e) => setField("street", e.target.value)}
                      disabled={!canEdit}
                    />
                  </label>

                  <div className="tmProfileGrid-3">
                    <label style={{ display: "grid", gap: 6 }}>
                      <div style={{ fontWeight: 600 }}>City *</div>
                      <input
                        className="tmInput"
                        value={form.city}
                        onChange={(e) => setField("city", e.target.value)}
                        disabled={!canEdit}
                      />
                    </label>

                    <label style={{ display: "grid", gap: 6 }}>
                      <div style={{ fontWeight: 600 }}>State *</div>
                      <input
                        className="tmInput"
                        value={form.state}
                        onChange={(e) => setField("state", e.target.value.toUpperCase())}
                        maxLength={20}
                        disabled={!canEdit}
                      />
                    </label>

                    <label style={{ display: "grid", gap: 6 }}>
                      <div style={{ fontWeight: 600 }}>ZIP *</div>
                      <input
                        className="tmInput"
                        value={form.zip}
                        onChange={(e) => setField("zip", formatZipInput(e.target.value))}
                        disabled={!canEdit}
                      />
                    </label>
                  </div>
                </div>
              </div>

              {providerFieldsVisible && (
                <div className="tmCard" style={{ marginTop: 12 }}>
                  <div className="tmCardHead">
                    <div>
                      <div className="tmCardTitle">Provider Information</div>
                      <div className="tmCardSub">Shown only for Physician, PA, and APRN roles</div>
                    </div>
                  </div>

                  <div style={{ padding: 12, display: "grid", gap: 10 }}>
                    <div className="tmProfileGrid-3">
                      <label style={{ display: "grid", gap: 6 }}>
                        <div style={{ fontWeight: 600 }}>NPI #</div>
                        <input
                          className="tmInput"
                          value={form.npiNumber}
                          onChange={(e) => setField("npiNumber", e.target.value)}
                          disabled={!canEdit}
                        />
                      </label>

                      <label style={{ display: "grid", gap: 6 }}>
                        <div style={{ fontWeight: 600 }}>DEA #</div>
                        <input
                          className="tmInput"
                          value={form.deaNumber}
                          onChange={(e) => setField("deaNumber", e.target.value)}
                          disabled={!canEdit}
                        />
                      </label>

                      <label style={{ display: "grid", gap: 6 }}>
                        <div style={{ fontWeight: 600 }}>Business Phone</div>
                        <input
                          className="tmInput"
                          value={form.businessPhone}
                          onChange={(e) =>
                            setField("businessPhone", formatPhoneInput(e.target.value))
                          }
                          placeholder="(555) 555-5555"
                          disabled={!canEdit}
                        />
                      </label>
                    </div>

                    <label style={{ display: "grid", gap: 6 }}>
                      <div style={{ fontWeight: 600 }}>Business Street</div>
                      <input
                        className="tmInput"
                        value={form.businessStreet}
                        onChange={(e) => setField("businessStreet", e.target.value)}
                        disabled={!canEdit}
                      />
                    </label>

                    <div className="tmProfileGrid-3">
                      <label style={{ display: "grid", gap: 6 }}>
                        <div style={{ fontWeight: 600 }}>Business City</div>
                        <input
                          className="tmInput"
                          value={form.businessCity}
                          onChange={(e) => setField("businessCity", e.target.value)}
                          disabled={!canEdit}
                        />
                      </label>

                      <label style={{ display: "grid", gap: 6 }}>
                        <div style={{ fontWeight: 600 }}>Business State</div>
                        <input
                          className="tmInput"
                          value={form.businessState}
                          onChange={(e) => setField("businessState", e.target.value.toUpperCase())}
                          disabled={!canEdit}
                        />
                      </label>

                      <label style={{ display: "grid", gap: 6 }}>
                        <div style={{ fontWeight: 600 }}>Business ZIP</div>
                        <input
                          className="tmInput"
                          value={form.businessZip}
                          onChange={(e) => setField("businessZip", formatZipInput(e.target.value))}
                          disabled={!canEdit}
                        />
                      </label>
                    </div>
                  </div>
                </div>
              )}

              <div className="tmCard" style={{ marginTop: 12 }}>
                <div className="tmCardHead">
                  <div>
                    <div className="tmCardTitle">Emergency Contact</div>
                    <div className="tmCardSub">Keep editable in every profile</div>
                  </div>
                </div>

                <div style={{ padding: 12, display: "grid", gap: 10 }}>
                  <div className="tmProfileGrid-3">
                    <label style={{ display: "grid", gap: 6 }}>
                      <div style={{ fontWeight: 600 }}>Emergency Contact Name</div>
                      <input
                        className="tmInput"
                        value={form.emergencyContactName}
                        onChange={(e) => setField("emergencyContactName", e.target.value)}
                        disabled={!canEdit}
                      />
                    </label>

                    <label style={{ display: "grid", gap: 6 }}>
                      <div style={{ fontWeight: 600 }}>Relationship</div>
                      <input
                        className="tmInput"
                        value={form.emergencyContactRelationship}
                        onChange={(e) => setField("emergencyContactRelationship", e.target.value)}
                        disabled={!canEdit}
                      />
                    </label>

                    <label style={{ display: "grid", gap: 6 }}>
                      <div style={{ fontWeight: 600 }}>Emergency Contact Phone</div>
                      <input
                        className="tmInput"
                        value={form.emergencyContactPhone}
                        onChange={(e) =>
                          setField("emergencyContactPhone", formatPhoneInput(e.target.value))
                        }
                        placeholder="(555) 555-5555"
                        disabled={!canEdit}
                      />
                    </label>
                  </div>
                </div>
              </div>

              <div className="tmCard" style={{ marginTop: 12 }}>
                <div className="tmCardHead">
                  <div>
                    <div className="tmCardTitle">Required Licenses / Certifications</div>
                    <div className="tmCardSub">
                      Required items are still tracked by position, but uploads are available for everyone now.
                    </div>
                  </div>
                </div>

                <div className="tmList tmListTight">
                  {requiredList.length === 0 ? (
                    <div className="tmEmpty" style={{ padding: 12 }}>
                      No required items for this position, but file uploads are still available below.
                    </div>
                  ) : (
                    checklist.map((c) => {
                      const status = c.missing ? "MISSING" : c.expired ? "EXPIRED" : "OK";
                      const statusColor = c.missing || c.expired ? "#b91c1c" : "#065f46";
                      const existing = credIndex.get(c.type);

                      return (
                        <div className="tmRow tmRowTight" key={c.type} style={{ alignItems: "center" }}>
                          <div>
                            <div className="tmRowTitle">
                              {c.type}{" "}
                              <span style={{ color: statusColor, fontWeight: 800 }}>
                                • {status}
                              </span>
                            </div>
                            <div className="tmRowMeta">
                              {c.type !== "ICS100" && (
                                <>Expires: {c.expiresAt ? formatDate(c.expiresAt) : "—"}</>
                              )}
                              {existing?.fileName ? ` • ${existing.fileName}` : ""}
                            </div>
                          </div>

                          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                            {existing?.fileURL ? (
                              <a className="tmBtn" href={existing.fileURL} target="_blank" rel="noreferrer">
                                View Card
                              </a>
                            ) : null}

                            <button
                              className="tmBtn"
                              onClick={() => openCredModal(c.type)}
                              disabled={credFileBusy || !canEdit}
                            >
                              {existing?.fileURL ? "Update" : "Upload"}
                            </button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              <div className="tmCard" style={{ marginTop: 12 }}>
                <div className="tmCardHead">
                  <div>
                    <div className="tmCardTitle">Additional File Uploads</div>
                    <div className="tmCardSub">Available in every profile, including Admins and Super Users</div>
                  </div>
                  <button className="tmBtn" onClick={() => openCredModal()} disabled={credFileBusy || !canEdit}>
                    Add / Update File
                  </button>
                </div>

                <div className="tmList tmListTight">
                  {creds.length === 0 ? (
                    <div className="tmEmpty" style={{ padding: 12 }}>
                      No files uploaded yet.
                    </div>
                  ) : (
                    [...creds]
                      .sort((a, b) => String(a?.type || "").localeCompare(String(b?.type || "")))
                      .map((c, idx) => (
                        <div className="tmRow tmRowTight" key={`${c?.type || "file"}-${idx}`} style={{ alignItems: "center" }}>
                          <div>
                            <div className="tmRowTitle">{normalizeType(c?.type) || "FILE"}</div>
                            <div className="tmRowMeta">
                              {c?.fileName || "Unnamed file"}
                              {c?.expiresAt ? ` • Expires: ${formatDate(c.expiresAt)}` : ""}
                            </div>
                          </div>

                          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                            {c?.fileURL ? (
                              <a className="tmBtn" href={c.fileURL} target="_blank" rel="noreferrer">
                                View File
                              </a>
                            ) : null}

                            <button
                              className="tmBtn"
                              onClick={() => openCredModal(c?.type)}
                              disabled={credFileBusy || !canEdit}
                            >
                              Update
                            </button>
                          </div>
                        </div>
                      ))
                  )}
                </div>
              </div>

              <div className="tmCard" style={{ marginTop: 12 }}>
                <div className="tmCardHead">
                  <div>
                    <div className="tmCardTitle">Profile Photo</div>
                    <div className="tmCardSub">Upload or take a photo</div>
                  </div>
                </div>

                <div style={{ padding: 12, display: "grid", gap: 10 }}>
                  {profile?.photoURL ? (
                    <img
                      src={profile.photoURL}
                      alt="Profile"
                      style={{ width: 140, height: 140, borderRadius: 12, objectFit: "cover" }}
                    />
                  ) : (
                    <div className="tmEmpty">No profile photo yet.</div>
                  )}

                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={(e) => handlePhotoFile(e.target.files?.[0])}
                    disabled={!canEdit}
                  />
                </div>
              </div>

              <div className="tmCard" style={{ marginTop: 12 }}>
                <div className="tmCardHead">
                  <div>
                    <div className="tmCardTitle">Signature</div>
                    <div className="tmCardSub">Sign and save for reuse</div>
                  </div>
                </div>

                <div style={{ padding: 12, display: "grid", gap: 10 }}>
                  {profile?.signatureURL ? (
                    <img
                      src={profile.signatureURL}
                      alt="Signature"
                      style={{
                        maxWidth: 320,
                        border: "1px solid #e5e7eb",
                        borderRadius: 10,
                        background: "white",
                      }}
                    />
                  ) : (
                    <div className="tmEmpty">No signature saved yet.</div>
                  )}

                  {!editingSignature && (
                    <button
                      className="tmBtn"
                      onClick={() => {
                        setEditingSignature(true);
                        setTimeout(() => resizeSignatureCanvas(false), 50);
                      }}
                      disabled={!canEdit}
                    >
                      Edit Signature
                    </button>
                  )}

                  {editingSignature && (
                    <>
                      <div
                        ref={sigWrapRef}
                        style={{
                          border: "2px solid #e5e7eb",
                          borderRadius: 12,
                          background: "white",
                          width: "100%",
                          maxWidth: 700,
                          height: 180,
                          overflow: "hidden",
                        }}
                      >
                        <canvas
                          ref={canvasRef}
                          style={{
                            width: "100%",
                            height: "100%",
                            display: "block",
                            background: "transparent",
                            touchAction: "none",
                          }}
                          onPointerDown={startDraw}
                          onPointerMove={moveDraw}
                          onPointerUp={endDraw}
                          onPointerCancel={endDraw}
                          onPointerLeave={endDraw}
                        />
                      </div>

                      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                        <button className="tmBtn" onClick={clearSignature} disabled={!canEdit}>
                          Clear
                        </button>

                        <button className="tmPrimaryBtn" onClick={handleSaveSignature} disabled={saving || !canEdit}>
                          {saving ? "Saving…" : "Save Signature"}
                        </button>

                        <button className="tmBtn" onClick={() => setEditingSignature(false)}>
                          Cancel
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {isEditingSelf && (
                <div className="tmCard" style={{ marginTop: 12 }}>
                  <div className="tmCardHead">
                    <div>
                      <div className="tmCardTitle">PIN</div>
                      <div className="tmCardSub">Optional — only available when editing your own profile</div>
                    </div>
                  </div>

                  <div style={{ padding: 12, display: "grid", gap: 10 }}>
                    <label style={{ display: "grid", gap: 6 }}>
                      <div style={{ fontWeight: 600 }}>PIN</div>
                      <input
                        className="tmInput"
                        value={form.pin}
                        onChange={(e) => setField("pin", e.target.value)}
                        placeholder="Enter new PIN"
                        disabled={!canEdit}
                      />
                      <div className="tmTileHint">We’ll store a hash, not the raw PIN.</div>
                    </label>
                  </div>
                </div>
              )}

              <div style={{ marginTop: 12 }}>
                <button className="tmPrimaryBtn" onClick={handleSaveEdits} disabled={saving || !canEdit}>
                  {saving ? "Saving…" : "Save Changes"}
                </button>
              </div>

              {credModalOpen && (
                <div className="tmModalBackdrop" onClick={closeCredModal} style={{ zIndex: 60 }}>
                  <div className="tmModal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
                    <div className="tmModalHeader">
                      <div className="tmModalTitle">Upload / Update File</div>
                      <button className="tmModalClose" onClick={closeCredModal}>
                        ✕
                      </button>
                    </div>

                    <div style={{ padding: 14, display: "grid", gap: 12 }}>
                      <label style={{ display: "grid", gap: 6 }}>
                        <div style={{ fontWeight: 700 }}>File type</div>
                        <select className="tmInput" value={credType} onChange={(e) => setCredType(e.target.value)}>
                          {allUploadTypes.map((t) => (
                            <option key={t} value={t}>
                              {t}
                            </option>
                          ))}
                        </select>
                      </label>

                      {credType !== "ICS100" && credType !== "OTHER" && credType !== "DRIVERS_LICENSE" && (
                        <label style={{ display: "grid", gap: 6 }}>
                          <div style={{ fontWeight: 700 }}>Expiration date</div>
                          <input
                            className="tmInput"
                            type="date"
                            value={credExpires}
                            onChange={(e) => setCredExpires(e.target.value)}
                          />
                          <div className="tmTileHint">Required for most certifications and licenses.</div>
                        </label>
                      )}

                      <label style={{ display: "grid", gap: 6 }}>
                        <div style={{ fontWeight: 700 }}>Choose file (PDF or photo)</div>
                        <input
                          className="tmInput"
                          type="file"
                          accept="image/*,application/pdf"
                          onChange={(e) => setCredFile(e.target.files?.[0] || null)}
                        />
                        <div className="tmTileHint">
                          {credFile
                            ? `Selected: ${credFile.name}`
                            : credIndex.get(normalizeType(credType))?.fileURL
                            ? "No new file selected. Saving will update expiration only."
                            : "No file selected."}
                        </div>
                      </label>

                      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                        <button className="tmBtn" onClick={closeCredModal} disabled={credFileBusy}>
                          Cancel
                        </button>
                        <button className="tmPrimaryBtn" onClick={handleSaveCredential} disabled={credFileBusy}>
                          {credFileBusy ? "Saving…" : "Save"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}